import { 
  GameSession, 
  Letter, 
  SessionStats, 
  FingerConfig,
  ProgressTile
} from '@/types';
import { TrainingMode } from '@/utils/trainingGenerator';
// 🔧 PHASE 1 FIX: Centralized state management with reducer pattern
export interface GameEngineState {
  session: GameSession | null;
  currentSequence: Letter[] | null;
  currentSequenceIndex: number;
  nextSequence: Letter[] | null;
  stats: SessionStats;
  isActive: boolean;
  timeRemaining: number;
  progressTiles: ProgressTile[];
  totalAttempts: number;
  wrongAttempts: number;
  // 🆕 Per-session mistakes keyed by expected letter (uppercase; space = '_').
  // Feeds the One Word live "Mistaken Letters" panel and the persisted race doc.
  letterMistakes: Record<string, number>;
  needsMoreSequences?: boolean; // 🔧 NEW: Flag to trigger sequence extension
  originalSessionContext: {
    dayNumber: number;
    roundNumber: number;
    trainingMode?: TrainingMode;
  } | null;
}

export type GameAction = 
  | { type: 'START_SESSION'; payload: { 
      fingerConfig: FingerConfig; 
      dayNumber: number; 
      trainingMode?: TrainingMode; 
      roundNumber?: number;
      letterSequences: Letter[][];
      // 🆕 NEW: Manual selection for word repetition
      manualSelection?: {
        selectedWords: string[];
        selectedPhrases: string[];
        randomizeOrder: boolean;
      };
    }}
  | { type: 'PROCESS_KEYSTROKE'; payload: { key: string; timestamp: number }}
  | { type: 'END_SESSION' }
  | { type: 'PAUSE_SESSION' }
  | { type: 'RESUME_SESSION' }
  | { type: 'UPDATE_TIMER'; payload: { timeRemaining: number }}
  | { type: 'EXTEND_SEQUENCES'; payload: { newSequences: Letter[][] }}
  | { type: 'RESET_STATE' };

const INITIAL_STATS: SessionStats = {
  totalLetters: 0,
  correctLetters: 0,
  incorrectLetters: 0,
  totalAttempts: 0,
  wrongAttempts: 0,
  accuracy: 0,
  speed: 0,
  duration: 0,
  wpm: 0
};

const ROUND_DURATION = 120000; // 2 minutes

export const initialGameState: GameEngineState = {
  session: null,
  currentSequence: null,
  currentSequenceIndex: 0,
  nextSequence: null,
  stats: INITIAL_STATS,
  isActive: false,
  timeRemaining: ROUND_DURATION,
  progressTiles: [],
  totalAttempts: 0,
  wrongAttempts: 0,
  letterMistakes: {},
  originalSessionContext: null
};

/**
 * 🔧 PERFORMANCE FIX: Calculate stats efficiently without deep cloning
 */
const calculateStatsOptimized = (
  completedLetters: Letter[], 
  duration: number, 
  totalAttempts: number, 
  wrongAttempts: number
): SessionStats => {
  const totalLetters = completedLetters.length;
  const correctLetters = completedLetters.filter(l => l.status === 'correct').length;
  const incorrectLetters = totalLetters - correctLetters;
  
  const accuracy = totalAttempts > 0 ? (correctLetters / totalAttempts) * 100 : 0;
  const speed = duration > 0 ? (correctLetters / (duration / 1000)) : 0;
  const wpm = duration > 0 ? (correctLetters / 5) / (duration / 60000) : 0;

  return {
    totalLetters,
    correctLetters,
    incorrectLetters,
    totalAttempts,
    wrongAttempts,
    accuracy,
    speed,
    duration,
    wpm
  };
};

/**
 * 🔧 CRITICAL FIX: Process keystroke without massive state reconstruction
 */
const processKeystrokeAction = (
  state: GameEngineState, 
  { key, timestamp }: { key: string; timestamp: number }
): GameEngineState => {
  if (!state.session || !state.isActive || !state.currentSequence) {
    return state;
  }

  const currentLetter = state.currentSequence[state.currentSequenceIndex];
  if (!currentLetter) {
    return state;
  }

  const expectedChar = currentLetter.char;
  const normalizedKey = key === ' ' ? '_' : key.toUpperCase();
  const normalizedExpected = expectedChar === '_' ? '_' : expectedChar.toUpperCase();
  const isCorrect = normalizedKey === normalizedExpected;

  const newProgressTile: ProgressTile = {
    id: `tile-${timestamp}-${Math.random().toString(36).substr(2, 9)}`,
    type: isCorrect ? 'correct' : 'incorrect',
    timestamp
  };

  const MAX_PROGRESS_TILES = 200;
  const updatedProgressTiles = [...state.progressTiles, newProgressTile];
  if (updatedProgressTiles.length > MAX_PROGRESS_TILES) {
    updatedProgressTiles.splice(0, updatedProgressTiles.length - MAX_PROGRESS_TILES);
  }

  const newTotalAttempts = state.totalAttempts + 1;
  const newWrongAttempts = state.wrongAttempts + (isCorrect ? 0 : 1);

  if (!isCorrect) {
    const allCompletedLetters = state.session.letterSequences
      .slice(0, state.session.currentSequenceIndex)
      .flat()
      .concat(state.currentSequence.slice(0, state.currentSequenceIndex));

    const duration = timestamp - state.session.startTime;
    const updatedStats = calculateStatsOptimized(allCompletedLetters, duration, newTotalAttempts, newWrongAttempts);

    // 🆕 Record which expected letter was mistaken (for live HUD + persisted race)
    const updatedLetterMistakes = {
      ...state.letterMistakes,
      [normalizedExpected]: (state.letterMistakes[normalizedExpected] || 0) + 1
    };

    return {
      ...state,
      stats: updatedStats,
      progressTiles: updatedProgressTiles,
      totalAttempts: newTotalAttempts,
      wrongAttempts: newWrongAttempts,
      letterMistakes: updatedLetterMistakes
    };
  }

  const updatedSequence = [...state.currentSequence];
  updatedSequence[state.currentSequenceIndex] = {
    ...currentLetter,
    status: 'correct'
  };

  let newCurrentSequenceIndex = state.currentSequenceIndex + 1;
  let newCurrentSequence = updatedSequence;
  let newNextSequence = state.nextSequence;
  let newSessionSequenceIndex = state.session.currentSequenceIndex;
  let needsMoreSequences = false;

  if (newCurrentSequenceIndex >= state.currentSequence.length) {
    newSessionSequenceIndex = state.session.currentSequenceIndex + 1;
    newCurrentSequenceIndex = 0;

    if (newSessionSequenceIndex < state.session.letterSequences.length) {
      newCurrentSequence = state.session.letterSequences[newSessionSequenceIndex];
      newNextSequence = state.session.letterSequences[newSessionSequenceIndex + 1] || null;
    } else {
      console.log('⚠️ Reached end of available sequences - need more sequences!');
      needsMoreSequences = true;
      newCurrentSequence = updatedSequence;
      newNextSequence = null;
    }
  }

  const updatedSession: GameSession = {
    ...state.session,
    letterSequences: state.session.letterSequences.map((seq, index) =>
      index === state.session!.currentSequenceIndex ? updatedSequence : seq
    ),
    currentSequenceIndex: newSessionSequenceIndex
  };

  const allCompletedLetters = updatedSession.letterSequences
    .slice(0, newSessionSequenceIndex)
    .flat()
    .concat(newCurrentSequence ? newCurrentSequence.slice(0, newCurrentSequenceIndex >= newCurrentSequence.length ? newCurrentSequence.length : newCurrentSequenceIndex) : []);

  const duration = timestamp - state.session.startTime;
  const updatedStats = calculateStatsOptimized(allCompletedLetters, duration, newTotalAttempts, newWrongAttempts);

  return {
    ...state,
    session: updatedSession,
    currentSequence: newCurrentSequence,
    currentSequenceIndex: newCurrentSequenceIndex,
    nextSequence: newNextSequence,
    stats: updatedStats,
    progressTiles: updatedProgressTiles,
    totalAttempts: newTotalAttempts,
    wrongAttempts: newWrongAttempts,
    needsMoreSequences: needsMoreSequences || false
  };
};

/**
 * 🔧 CRITICAL FIX: Centralized state reducer prevents re-render loops
 */
export const gameStateReducer = (state: GameEngineState, action: GameAction): GameEngineState => {
  
  const newState = (() => {
    switch (action.type) {
    case 'START_SESSION': {
      const { fingerConfig, dayNumber, trainingMode, roundNumber, letterSequences, manualSelection } = action.payload;
      
      const newSession: GameSession = {
        id: `session-${Date.now()}`,
        startTime: Date.now(),
        letterSequences,
        currentSequenceIndex: 0,
        isActive: true,
        dayNumber,
        activeFingersConfig: fingerConfig,
        // 🆕 NEW: Store manual selection context for word repetition
        manualSelection,
        trainingMode: trainingMode ? {
          complexity: trainingMode.complexity,
          hands: trainingMode.hands
        } : undefined
      };

      return {
        ...initialGameState,
        session: newSession,
        currentSequence: letterSequences[0] || null,
        currentSequenceIndex: 0,
        nextSequence: letterSequences[1] || null,
        isActive: true,
        timeRemaining: ROUND_DURATION,
        originalSessionContext: {
          dayNumber,
          roundNumber: roundNumber || 1,
          trainingMode
        }
      };
    }

    case 'PROCESS_KEYSTROKE':
      return processKeystrokeAction(state, action.payload);

    case 'END_SESSION':
      if (!state.session) return state;
      
      const endTime = Date.now();
      const duration = endTime - state.session.startTime;
      
      // Calculate final stats
      const allLetters = state.session.letterSequences.flat();
      const completedLetters = allLetters.filter(letter => letter.status !== 'neutral');
      const finalStats = calculateStatsOptimized(completedLetters, duration, state.totalAttempts, state.wrongAttempts);

      return {
        ...state,
        session: {
          ...state.session,
          endTime,
          isActive: false
        },
        isActive: false,
        stats: finalStats
      };

    case 'PAUSE_SESSION':
      return {
        ...state,
        isActive: false
      };

    case 'RESUME_SESSION':
      return {
        ...state,
        isActive: true
      };

    case 'UPDATE_TIMER':
      return {
        ...state,
        timeRemaining: action.payload.timeRemaining
      };

    case 'EXTEND_SEQUENCES': {
      if (!state.session) return state;
      
      const extendedSequences = [...state.session.letterSequences, ...action.payload.newSequences];
      
      // 🔧 CRITICAL FIX: Properly update current and next sequences when extending
      let newCurrentSequence = state.currentSequence;
      let newNextSequence = state.nextSequence;
      
      // If we're at the end of sequences or don't have a next sequence, update them
      const currentIndex = state.session.currentSequenceIndex;
      
      if (!newCurrentSequence && currentIndex < extendedSequences.length) {
        newCurrentSequence = extendedSequences[currentIndex];
      }
      
      if (!newNextSequence && currentIndex + 1 < extendedSequences.length) {
        newNextSequence = extendedSequences[currentIndex + 1];
      }
      
      console.log(`✅ Extended sequences: ${state.session.letterSequences.length} -> ${extendedSequences.length}`);
      console.log(`🔄 Current sequence updated: ${!!newCurrentSequence}, Next sequence updated: ${!!newNextSequence}`);
      
      return {
        ...state,
        session: {
          ...state.session,
          letterSequences: extendedSequences
        },
        currentSequence: newCurrentSequence,
        nextSequence: newNextSequence,
        needsMoreSequences: false // 🔧 Clear flag after extending sequences
      };
    }

    case 'RESET_STATE':
      return initialGameState;

    default:
      return state;
    }
  })();
  
  // 🔧 PHASE 1 FIX: Monitor state transitions for performance
  // DISABLED: Excessive logging causing performance issues
  /*
  if (process.env.NODE_ENV === 'development') {
    debugManager.logStateTransition(action.type, state, newState, startTime);
  }
  */
  
  return newState;
};
